<template>
	<div v-if="steps.length || busy" class="CopilotTimeline" data-cy="copilot-timeline">
		<button v-if="steps.length" type="button" class="CopilotTimeline__toggle" data-cy="copilot-timeline-toggle" :aria-expanded="open" @click="open = !open">
			<v-icon size="16" class="mr-2">{{ open ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
			{{ $t('text.copilotTimeline') }} ({{ steps.length }})
		</button>
		<div v-if="open" class="CopilotTimeline__steps">
			<div v-for="(step, index) of steps" :key="index" class="CopilotTimeline__step" :class="`CopilotTimeline__step--${step.status}`" data-cy="copilot-step" :data-status="step.status">
				<div class="CopilotTimeline__head">
					<button type="button" class="CopilotTimeline__expand" data-cy="copilot-step-expand" :aria-expanded="isExpanded(index)" :aria-label="$t('text.copilotStepDetails')" @click="toggle(index)">
						<v-icon size="14">{{ isExpanded(index) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
					</button>
					<span class="CopilotTimeline__tool">{{ step.tool }}</span>
					<span v-if="isRunning(step)" class="CopilotTimeline__chip CopilotTimeline__chip--running" data-cy="copilot-step-status">{{ $t('text.copilotStepRunning') }}</span>
					<span v-else-if="hasStatus(step)" class="CopilotTimeline__chip" :class="`CopilotTimeline__chip--${step.status}`" data-cy="copilot-step-status">{{ $t(`text.${STATUS_KEYS[step.status] ?? STATUS_KEYS.ok}`) }}</span>
				</div>
				<template v-if="!isExpanded(index)">
					<span v-if="step.reason" class="CopilotTimeline__text">{{ step.reason }}</span>
					<span v-if="step.error" class="CopilotTimeline__error">{{ step.error }}</span>
					<span v-else-if="step.note" class="CopilotTimeline__text">{{ step.note }}</span>
				</template>
				<div v-else class="CopilotTimeline__details" data-cy="copilot-step-details">
					<span v-if="step.reason" class="CopilotTimeline__text">{{ step.reason }}</span>
					<span v-if="argumentsOf(step)" class="CopilotTimeline__meta">{{ $t('text.copilotStepArguments') }}: {{ argumentsOf(step) }}</span>
					<span v-if="step.kind === 'tool_call'" class="CopilotTimeline__meta">
						{{ step.latency_ms ?? 0 }} ms · {{ $t('text.copilotStepResultSize') }}: {{ step.result_size ?? 0 }}{{ step.result_truncated ? '+' : '' }}
					</span>
					<span v-if="step.note" class="CopilotTimeline__text">{{ step.note }}</span>
					<span v-if="step.error" class="CopilotTimeline__error">{{ step.error }}</span>
					<ul v-if="step.rejections?.length" class="CopilotTimeline__rejections">
						<li v-for="(rejection, at) of step.rejections" :key="at" data-cy="copilot-step-rejection">
							<span class="CopilotTimeline__path">{{ rejection.op }} {{ rejection.path }}</span>
							<span class="CopilotTimeline__error">{{ rejection.errors.join('; ') }}</span>
						</li>
					</ul>
					<span v-if="step.kind !== 'tool_call' && step.result" class="CopilotTimeline__text">{{ step.result }}</span>
				</div>
			</div>
		</div>
		<div v-if="busy" class="CopilotTimeline__activity" role="status" data-cy="copilot-activity">
			<v-progress-circular indeterminate size="14" width="2" class="mr-2 flex-shrink-0" />
			<span>{{ activity }}</span>
		</div>
	</div>
</template>

<script lang="js">
// The run timeline of the Copilot drawer (ticket 16): collapsed by default; each tool call carries a
// status chip (ok, error, refused) and expands to its details: the agent's one-sentence reason, the
// arguments in short form, latency and result size, the error and, for a proposal call, every
// rejected operation with its path and errors. Reasoning and assistant steps expand to their text.
// While a run is busy, an activity line under the steps says what is going on from the moment the user
// sends: the reason of the tool call that is running (the service publishes a call as it starts, with
// `running`), else `pendingLabel` (the drawer's guess from the turn: reading a page, an attachment, thinking).
const STATUS_KEYS = { ok: 'copilotStepOk', error: 'copilotStepError', refused: 'copilotStepRefused' }
const TEXT_CAP = 80

// Arguments as `key: value` pairs: lists by their length, long strings cut, objects elided.
export function shortArguments(args) {
	return Object.entries(args ?? {})
		.filter(([ , value ]) => value !== null && value !== undefined && value !== '')
		.map(([ key, value ]) => {
			if (Array.isArray(value)) return `${key}: ${value.length}`
			if (typeof value === 'object') return `${key}: {…}`
			const text = String(value)
			return `${key}: ${text.length > TEXT_CAP ? text.slice(0, TEXT_CAP) + '…' : text}`
		})
		.join(', ')
}

export default {
	name: 'CopilotTimeline',
	props: {
		/** TrajectoryStep[] of the running or last run (copilot-client.ts) */
		steps: { type: Array, default: () => [] },
		/** a run is streaming: the activity line shows */
		busy: { type: Boolean, default: false },
		/** what the activity line says while no tool call is running */
		pendingLabel: { type: String, default: '' },
	},
	data: () => ({
		open: false,
		expanded: [],
		STATUS_KEYS,
	}),
	watch: {
		// a new run starts with an empty list: nothing of the last run stays expanded
		'steps.length'(length, before) {
			if (length < before) this.expanded = []
		},
	},
	computed: {
		activity() {
			const running = [ ...this.steps ].reverse().find(step => this.isRunning(step))
			return running ? running.reason || running.tool : this.pendingLabel
		},
	},
	methods: {
		isRunning(step) {
			return this.busy && step.kind === 'tool_call' && step.running === true
		},
		hasStatus(step) {
			return step.kind === 'tool_call' || step.status !== 'ok'
		},
		isExpanded(index) {
			return this.expanded.includes(index)
		},
		toggle(index) {
			this.expanded = this.isExpanded(index) ? this.expanded.filter(i => i !== index) : [ ...this.expanded, index ]
		},
		argumentsOf(step) {
			return shortArguments(step.arguments)
		},
	},
}
</script>

<style scoped lang="scss">
.CopilotTimeline {
	font-size: 12px;
	color: #616161;
	&__toggle {
		display: flex;
		align-items: center;
		background: none;
		border: 0;
		padding: 2px 0;
		cursor: pointer;
		font: inherit;
		color: inherit;
	}
	&__steps {
		border-left: 2px solid #e0e0e0;
		margin: 4px 0 0 7px;
		padding-left: 10px;
		display: flex;
		flex-direction: column;
		gap: 4px;
	}
	&__step {
		display: flex;
		flex-direction: column;
		&--refused .CopilotTimeline__tool,
		&--error .CopilotTimeline__tool { color: #c62828; }
	}
	&__head {
		display: flex;
		align-items: center;
		gap: 4px;
	}
	&__expand {
		display: flex;
		background: none;
		border: 0;
		padding: 0;
		cursor: pointer;
		color: inherit;
	}
	&__tool {
		font-family: monospace;
		font-size: 11px;
		color: #424242;
	}
	&__chip {
		border-radius: 8px;
		padding: 0 6px;
		font-size: 10px;
		line-height: 16px;
		background: #e8f5e9;
		color: #2e7d32;
		&--error { background: #ffebee; color: #c62828; }
		&--refused { background: #fff3e0; color: #e65100; }
		&--running { background: #ede7f6; color: #5e35b1; }
	}
	&__activity {
		display: flex;
		align-items: flex-start;
		margin-top: 4px;
		color: #5e35b1;
		word-break: break-word;
	}
	&__details {
		display: flex;
		flex-direction: column;
		gap: 2px;
		padding-left: 18px;
	}
	&__meta {
		color: #757575;
		word-break: break-word;
	}
	&__text {
		word-break: break-word;
		white-space: pre-line;
	}
	&__error {
		color: #c62828;
		word-break: break-word;
	}
	&__rejections {
		margin: 2px 0 0;
		padding-left: 14px;
		li {
			display: flex;
			flex-direction: column;
		}
	}
	&__path {
		font-family: monospace;
		font-size: 11px;
		word-break: break-all;
	}
}
</style>
